import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import _ from '@lodash';
import { FuseScrollbars } from '@fuse';
import {
	Checkbox,
	CircularProgress,
	Icon,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	MenuList,
	Table,
	TableBody,
	TableCell,
	TablePagination,
	TableRow,
	Typography
} from '@material-ui/core';
import * as Actions from './store/actions';
import * as ActionsDialog from 'app/store/actions';

import { FuseAnimate } from '@fuse';

import NodesTableHead from './NodesTableHead';

import Error from 'app/fuse-layouts/shared-components/Error';
import NodeDisplay from './NodeDisplay';
import { isParticipant } from 'app/main/util/enums';

const NodesTable = props => {
	const dispatch = useDispatch();

	const [selected, setSelected] = useState([]);
	const [tableSelected, setTableSelected] = useState(null);
	const [data, setData] = useState(null);
	const [page, setPage] = useState(Number(localStorage.getItem('N-page')) || 0);
	const [rowsPerPage, setRowsPerPage] = useState(Number(localStorage.getItem('N-rowsPerPage')) || 10);
	const [order, setOrder] = useState({ direction: 'asc', id: null });
	const [selectedMenu, setSelectedMenu] = useState(null);

	const cluster = useSelector(({ clusters }) => clusters.cluster.cluster);
	const node = useSelector(({ clusters }) => clusters.cluster.node);
	const user = useSelector(({ auth }) => auth.user);
	const isUserParticipant = isParticipant(user);

	useEffect(() => {
		setData(props.nodes.data);
	}, [props.nodes.data]);

	useEffect(() => {
		setPage(0);
	}, [props.filter]);

	const handleRequestSort = (event, property) => {
		if (order.id === property) {
			if (order.direction === 'desc') {
				setOrder({ direction: 'asc', id: property });
			} else {
				setOrder({ direction: 'desc', id: null });
			}
		} else {
			setOrder({ direction: 'desc', id: property });
		}
	};

	const busyNodeStates = ['Running', 'Busy', 'Destroying', 'Deploying']

	const resetSelected = () => setSelected([]);

	const resetTableSelected = () => setTableSelected(null);

	const handleCheck = name => {
		const selectedIndex = selected.indexOf(name);
		let newSelected = [];

		if (selectedIndex === -1) {
			newSelected = newSelected.concat(selected, name);
		} else if (selectedIndex === 0) {
			newSelected = newSelected.concat(selected.slice(1));
		} else if (selectedIndex === selected.length - 1) {
			newSelected = newSelected.concat(selected.slice(0, -1));
		} else if (selectedIndex > 0) {
			newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
		}

		setSelected(newSelected);
	};

	const handleSelectAllClick = event => {
		if (event.target.checked) {
			setSelected(data.map(n => n.name));
			return;
		}
		setSelected([]);
	};

	const openSelectedMenu = event => {
		event.stopPropagation();
		setSelectedMenu(event.currentTarget);
	};

	const closeSelectedMenu = () => {
		setSelectedMenu(null);
		resetTableSelected();
	};

	const handleChangePage = (event, page) => {
		setPage(page);
		localStorage.setItem('N-page', page);
	};

	const handleChangeRowsPerPage = event => {
		setRowsPerPage(event.target.value);
		setPage(page);
		localStorage.setItem('N-rowsPerPage', event.target.value);
	};

	const handleDetails = name => {
		const data = {
			node: name,
			cluster: cluster.id
		};
		dispatch(Actions.getNode(data)).then(() =>
			dispatch(
				ActionsDialog.openDialog({
					children: <NodeDisplay node={node} mgr={cluster.data.manager} />,
					maxWidth: 'md',
					fullWidth: true,
					onClose: (event, reason) => {
						if (reason !== 'backdropClick') {
							dispatch(ActionsDialog.closeDialog());
						}
					}
				})
			)
		);
		closeSelectedMenu();
	};

	const handleTransition = (selected, type) => {
		const action =
			type === 'deploy'
				? dispatch(Actions.deployNode(cluster.id, [selected]))
				: dispatch(Actions.destroyNode(cluster.id, [selected]));
		action.catch(err =>
			dispatch(
				ActionsDialog.openDialog({
					children: <Error message={`${err.data.message}! Refer to logs...`} />,
					maxWidth: 'xs',
					fullWidth: true,
					onClose: (event, reason) => {
						if (reason !== 'backdropClick') {
							dispatch(ActionsDialog.closeDialog());
						}
					}
				})
			)
		);
		closeSelectedMenu();
	};

	if (data && data.length === 0 && props.filter) {
		let display = '';
		if (props.filter === 'A') display = 'Available';
		else if (props.filter === 'B') display = 'Busy';
		else if (props.filter === 'P') display = 'Deploying';
		else if (props.filter === 'S') display = 'Destroying';
		else if (props.filter === 'D') display = 'Down';
		else if (props.filter === 'O') display = 'Offline';
		else display = '';
		return (
			<FuseAnimate animation="transition.slideUpIn" delay={300}>
				<div className="flex flex-1 items-center justify-center h-full">
					<Typography color="textSecondary" variant="h5">
						{`There are no ${display} Nodes...`}
					</Typography>
				</div>
			</FuseAnimate>
		);
	}

	return (
		<React.Fragment>
			{data && (
				<FuseAnimate animation="transition.slideUpIn" delay={300}>
					<div className="w-full h-full flex flex-col border-1 rounded-8">
						<div className="flex-grow overflow-x-auto overflow-y-auto">
							<Table stickyHeader aria-labelledby="tableTitle">
								<NodesTableHead
									numSelected={selected.length}
									order={order}
									onSelectAllClick={handleSelectAllClick}
									onRequestSort={handleRequestSort}
									rowCount={data.length}
									resetSelected={resetSelected}
									selected={selected}
									isUserParticipant={isUserParticipant}
								/>

								<TableBody>
									{(order.id ? _.orderBy(
										data,
										[
											o => {
												if (order.id === 'state') return o.state.state;
												if (order.id === 'cores') return Number(o.cores.split(':')[0]);
												else return o[order.id];
											}
										],
										[order.direction]
									) : data)
										.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
										.map(n => {
											const isSelected = selected.indexOf(n.name) !== -1;
											return (
												<TableRow
													className="h-64"
													role="checkbox"
													aria-checked={isSelected}
													tabIndex={-1}
													id={n.name}
													key={n.name}
													selected={isSelected}
												>
													{!isUserParticipant && (
													<TableCell id={`node-checkbox-${n.name}`} className="w-48 px-4 sm:px-12" padding="checkbox">
														<Checkbox
															checked={isSelected}
															onClick={event => event.stopPropagation()}
															onChange={() => handleCheck(n.name)}
														/>
													</TableCell>
													)}

													<TableCell className="w-52" component="th" scope="row" padding="none"></TableCell>

													<TableCell id={`node-name-${n.name}`} className="truncate" component="th" scope="row">
														<Typography className="font-bold text-16">{n.name}</Typography>
													</TableCell>

													<TableCell id={`node-instance-type-${n.name}`} className="truncate" component="th" scope="row">
														<Typography className="font-bold text-16">{n.instance_id}</Typography>
													</TableCell>

													<TableCell id={`node-state-${n.name}`} component="th" scope="row" align="left">
														<div className="flex items-center">
															<div className="pr-2">
																{n.state.state === 'Deploying' || n.state.state === 'Destroying' ? (
																	<CircularProgress size={20} thickness={5} />
																) : (
																	<Icon className={`text-${n.state.color} text-20`}>{n.state.icon}</Icon>
																)}
															</div>
															<div className="pb-1 font-bold">
																{n.state.state === 'Running' ? 'Busy' : n.state.state}
															</div>
														</div>
													</TableCell>

													<TableCell id={`node-cores-${n.name}`} component="th" scope="row" align="center">
														<Typography className="font-bold">{n.cores}</Typography>
													</TableCell>

													<TableCell id={`node-threads-${n.name}`} component="th" scope="row" align="center">
														<Typography className="font-bold">{n.loadAvg}</Typography>
													</TableCell>

													{!isUserParticipant && (
													<TableCell
														id={`${n.name}-actions`}
														align="center"
														style={{
															width: 100,
															minWidth: 100,
															maxWidth: 100,
															position: 'sticky',
															right: 0,
															backgroundColor: '#fff',
															zIndex: 1,
															boxShadow: '-2px 0 5px -2px rgba(0,0,0,0.1)'
														}}
													>
														<div>
															<IconButton
																id={`${n.name}-actions-more-btn`}
																className="cursor-pointer"
																aria-owns={selectedMenu ? 'selectedMenu' : null}
																aria-haspopup="true"
																onClick={e => {
																	setTableSelected(n);
																	openSelectedMenu(e);
																}}
															>
																<Icon>more_horiz</Icon>
															</IconButton>
															<Menu
																id="nodes_table_selectedMenu"
																anchorEl={selectedMenu}
																open={Boolean(selectedMenu)}
																onClose={closeSelectedMenu}
															>
																<MenuList>
																	<MenuItem
																		id="nodes_table_actions-more-deploy"
																		onClick={() => handleDetails(tableSelected?.name)}
																	>
																		<ListItemIcon className="min-w-40">
																			<Icon>info</Icon>
																		</ListItemIcon>
																		<ListItemText primary="Details" />
																	</MenuItem>


																	<MenuItem
																		id="nodes_table_actions-more-deploy"
																		disabled={!tableSelected || busyNodeStates.includes(tableSelected?.state.state)}
																		onClick={() => handleTransition(tableSelected?.name, 'deploy')}
																	>
																		<ListItemIcon className="min-w-40">
																			<Icon>cloud_upload</Icon>
																		</ListItemIcon>
																		<ListItemText primary="Deploy" />
																	</MenuItem>
																	<MenuItem
																		id="nodes_table_actions-more-destroy"
																		disabled={!tableSelected || busyNodeStates.includes(tableSelected?.state.state)}
																		onClick={() => handleTransition(tableSelected?.name, 'destroy')}
																	>
																		<ListItemIcon className="min-w-40">
																			<Icon>cloud_download</Icon>
																		</ListItemIcon>
																		<ListItemText primary="Destroy" />
																	</MenuItem>
																</MenuList>
															</Menu>
														</div>
													</TableCell>
													)}
												</TableRow>
											);
										})}
								</TableBody>
							</Table>
						</div>

						<TablePagination
							className="mt-auto flex-shrink-0"
							component="div"
							count={data.length}
							rowsPerPage={rowsPerPage}
							page={page}
							backIconButtonProps={{ 'aria-label': 'Previous Page' }}
							nextIconButtonProps={{ 'aria-label': 'Next Page' }}
							onPageChange={handleChangePage}
							onRowsPerPageChange={handleChangeRowsPerPage}
						/>
					</div>
				</FuseAnimate>
			)}
		</React.Fragment>
	);
};

export default withRouter(NodesTable);
